import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,        // NEW: For the large image preview
  DialogContent, // NEW: For the content within the dialog
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { baseUrl } from '../utils/config';

const ExcelUploader = () => {
  const [mergedCellsInfo, setMergedCellsInfo] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [fileTypeMessage, setFileTypeMessage] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ message: '', type: '', createdAt: null });
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFileType, setUploadedFileType] = useState('');
  const [parsingLoading, setParsingLoading] = useState(false);

  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageUploadError, setImageUploadError] = useState(null);

  // --- NEW STATE for Big Image Preview ---
  const [openBigImagePreview, setOpenBigImagePreview] = useState(false);

  const excelFileInputRef = useRef(null);
  const imageFileInputRef = useRef(null);

  // Helper function to convert Excel's decimal time to HH:MM:SS
  const convertDecimalToTime = (decimalTime) => {
    const num = parseFloat(decimalTime);
    if (isNaN(num) || num < 0 || num >= 1) {
      return String(decimalTime);
    }
    const totalHours = num * 24;
    const roundedHours = Math.round(totalHours);
    const finalHours = roundedHours % 24;
    const formattedHours = finalHours.toString().padStart(2, '0');
    return `${formattedHours}:00:00`;
  };

  const handleFileUpload = (event) => {
    setFileError(null);
    setFileTypeMessage(null);
    setMergedCellsInfo([]);
    setTableData([]);
    setUploadStatus({ message: '', type: '', createdAt: null });
    setUploadedFileName('');
    setUploadedFileType('');
    setParsingLoading(true);

    const file = event.target.files && event.target.files[0];
    if (!file) {
      setFileError("No file selected.");
      setParsingLoading(false);
      return;
    }

    const reader = new FileReader();
    const fileExtension = file.name.split('.').pop().toLowerCase();

    reader.onload = (e) => {
      try {
        let workbook;
        let worksheet;

        if (fileExtension === 'csv') {
          setFileTypeMessage("CSV file uploaded. Merged cells and complex formatting are not applicable for CSV files.");
          const data = e.target.result;
          workbook = XLSX.read(data, { type: 'string' });
          worksheet = workbook.Sheets[workbook.SheetNames[0]] || {};

          setMergedCellsInfo([]);

          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });
          const processedTableData = json.map(row =>
            row.map(cell => ({
              value: cell !== undefined ? cell : '',
              rowspan: 1,
              colspan: 1,
              isMergedTopLeft: false,
              mergeRange: null,
              isCellBold: false,
              fontColor: null,
              fillColor: null,
            }))
          );
          setTableData(processedTableData.map(row => ({ cells: row, isRowBold: false })));
          setUploadedFileName(file.name);
          setUploadedFileType(fileExtension);

        } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: 'array', cellStyles: true });
          worksheet = workbook.Sheets[workbook.SheetNames[0]] || {};

          const merges = worksheet['!merges'] || [];
          const decodedMerges = merges.map(merge => ({
            range: XLSX.utils.encode_range(merge),
            s: merge.s,
            e: merge.e,
          }));

          const range = XLSX.utils.decode_range(worksheet['!ref'] || "A1");
          const numRows = range.e.r + 1;
          const numCols = range.e.c + 1;

          const tempProcessedRows = [];
          const rowBoldStatus = [];

          const skipCells = new Set();

          for (let r = 0; r < numRows; r++) {
            const row = [];
            let currentRowHasBold = false;
            for (let c = 0; c < numCols; c++) {
              if (skipCells.has(`${r},${c}`)) {
                row.push(null);
                continue;
              }

              let cellContent = '';
              let rowspan = 1;
              let colspan = 1;
              let isMergedCellTopLeft = false;
              let mergeRangeObj = null;

              for (const merge of decodedMerges) {
                if (merge.s.r === r && merge.s.c === c) {
                  isMergedCellTopLeft = true;
                  mergeRangeObj = merge;
                  rowspan = merge.e.r - merge.s.r + 1;
                  colspan = merge.e.c - merge.s.c + 1;

                  for (let mr = merge.s.r; mr <= merge.e.r; mr++) {
                    for (let mc = merge.s.c; mc <= merge.e.c; mc++) {
                      if (!(mr === r && mc === c)) {
                        skipCells.add(`${mr},${mc}`);
                      }
                    }
                  }
                  break;
                }
              }

              const cellAddress = XLSX.utils.encode_cell({ r, c });
              const cell = worksheet[cellAddress];

              let isCellBold = false;
              if (cell && cell.s && cell.s.font && cell.s.font.b === 1) {
                  isCellBold = true;
                  currentRowHasBold = true;
              }
              
              let fontColor = null;
              if (cell && cell.s && cell.s.font && cell.s.font.color && cell.s.font.color.rgb) {
                  fontColor = '#' + cell.s.font.color.rgb.slice(2);
              }

              let fillColor = null;
              if (cell && cell.s) {
                  if (cell.s.bgColor && cell.s.bgColor.rgb) {
                      fillColor = '#' + cell.s.bgColor.rgb.slice(2);
                  } else if (cell.s.fgColor && cell.s.fgColor.rgb) {
                      fillColor = '#' + cell.s.fgColor.rgb.slice(2);
                  }
                  else if (cell.s.fill) {
                      if (cell.s.fill.fgColor && cell.s.fill.fgColor.rgb) {
                          fillColor = '#' + cell.s.fill.fgColor.rgb.slice(2);
                      } else if (cell.s.fill.bgColor && cell.s.fill.bgColor.rgb) {
                          fillColor = '#' + cell.s.fill.bgColor.rgb.slice(2);
                      }
                  }
              }

              if (cell && cell.v !== undefined) {
                const cellAddressLog = XLSX.utils.encode_cell({ r, c });
                console.log(`Cell ${cellAddressLog} Value:`, cell.v);
                console.log(`Cell ${cellAddressLog} Style Object (cell.s):`, cell.s);
                console.log(`Cell ${cellAddressLog} isCellBold (after parsing):`, isCellBold);
                console.log(`Cell ${cellAddressLog} fillColor (after parsing):`, fillColor);
              }
              
              if (cell && cell.v !== undefined) {
                cellContent = cell.v;

                if (cell.t === 'd' && cell.v instanceof Date) {
                  cellContent = cell.v.toLocaleDateString();
                } else if (cell.t === 'n') {
                  const numericCellValue = parseFloat(cell.v);

                  if (XLSX.SSF.is_date(cell.v)) {
                    const totalDays = cell.v;
                    const datePart = Math.floor(totalDays);
                    const timePart = totalDays - datePart;

                    if (timePart > 0 && timePart < 1 && datePart === 0) {
                      cellContent = convertDecimalToTime(timePart);
                    } else if (timePart > 0 && timePart < 1 && datePart > 0) {
                       const date = XLSX.SSF.format('mm/dd/yyyy', cell.v);
                       const time = convertDecimalToTime(timePart);
                       cellContent = `${date} ${time}`;
                    }
                    else {
                      cellContent = XLSX.SSF.format('mm/dd/yyyy', cell.v);
                    }

                  } else if (numericCellValue >= 0 && numericCellValue < 1 ) {
                    cellContent = convertDecimalToTime(numericCellValue);
                  }
                }
              }

              row.push({
                value: cellContent,
                rowspan: rowspan,
                colspan: colspan,
                isMergedTopLeft: isMergedCellTopLeft,
                mergeRange: mergeRangeObj ? mergeRangeObj.range : null,
                isCellBold: isCellBold,
                fontColor: fontColor,
                fillColor: fillColor,
              });
            }
            tempProcessedRows.push(row);
            rowBoldStatus.push(currentRowHasBold);
          }

          const finalTableData = tempProcessedRows.map((row, index) => ({
            cells: row,
            isRowBold: rowBoldStatus[index],
          }));
          setTableData(finalTableData);
          setFileTypeMessage("Excel file uploaded. Applying custom formatting rules.");
          setUploadedFileName(file.name);
          setUploadedFileType(fileExtension);

        } else {
          setFileError("Unsupported file type. Please upload a .xlsx, .xls, or .csv file.");
          if (excelFileInputRef.current) {
            excelFileInputRef.current.value = "";
          }
          return;
        }

      } catch (error) {
        console.error("An error occurred during file processing:", error);
        setFileError("Failed to read the file. Please ensure it's a valid spreadsheet format.");
      } 
      finally {
        setParsingLoading(false);
      }
    };

    if (fileExtension === 'csv') {
      reader.readAsText(file);
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      reader.readAsArrayBuffer(file);
    } else {
      setParsingLoading(false);
    }
  };

  const handleImageUpload = (event) => {
    setImageUploadError(null);
    const file = event.target.files && event.target.files[0];

    if (!file) {
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      return;
    }

    const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
    if (!acceptedImageTypes.includes(file.type)) {
      setImageUploadError("Unsupported image type. Please upload a JPG, PNG, GIF, BMP, or WebP image.");
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      if (imageFileInputRef.current) {
        imageFileInputRef.current.value = "";
      }
      return;
    }

    setSelectedImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleClearImage = () => {
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageUploadError(null);
    setOpenBigImagePreview(false); // Close big preview if open
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = "";
    }
  };

  // --- NEW: Open/Close handlers for Big Image Preview ---
  const handleOpenBigImagePreview = () => {
    if (imagePreviewUrl) { // Only open if there's an image to preview
      setOpenBigImagePreview(true);
    }
  };

  const handleCloseBigImagePreview = () => {
    setOpenBigImagePreview(false);
  };


  const handleCellChange = (newValue, rowIndex, colIndex) => {
    setTableData(prevTableData => {
      const newTableData = [...prevTableData];
      const newRowObj = { ...newTableData[rowIndex] };
      const newCells = [...newRowObj.cells];

      if (newCells[colIndex]) {
          newCells[colIndex] = { ...newCells[colIndex], value: newValue };
      } else {
          console.warn(`Attempted to edit a null cell at [${rowIndex}, ${colIndex}]`);
          return prevTableData;
      }

      newRowObj.cells = newCells;
      newTableData[rowIndex] = newRowObj;
      return newTableData;
    });
  };

  const handleUploadToDB = async () => {
    if (tableData.length === 0 && !selectedImageFile) {
      setUploadStatus({ message: 'No data or image to upload. Please upload an Excel/CSV file or an image first.', type: 'error', createdAt: null });
      return;
    }
    if (tableData.length > 0 && (!uploadedFileName || !uploadedFileType)) {
        setUploadStatus({ message: 'Excel/CSV file name or type missing. Please re-upload the file.', type: 'error', createdAt: null });
        return;
    }

    setIsSaving(true);
    setUploadStatus({ message: 'Saving data to database...', type: 'info', createdAt: null });

    try {
      const token = localStorage.getItem('authToken'); 

      if (!token) {
        setUploadStatus({ message: 'Authentication token missing. Please log in again.', type: 'error', createdAt: null });
        setIsSaving(false);
        return;
      }

      const formData = new FormData();
      formData.append('fileName', uploadedFileName);
      console.log(uploadedFileName);
      formData.append('fileType', uploadedFileType);
      formData.append('tableData', JSON.stringify(tableData));

      if (selectedImageFile) {
        formData.append('uploadedImage', selectedImageFile);
      }

      const response = await fetch(`${baseUrl}/api/save-form-template-with-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setUploadStatus({
          message: data.message || 'Data and image saved successfully!',
          type: 'success',
          createdAt: data.createdAt
        });
        // Optionally clear data after successful upload
        // setTableData([]);
        // setUploadedFileName('');
        // setUploadedFileType('');
        // handleClearImage();
      } else {
        setUploadStatus({
          message: data.message || 'Failed to save data. Please try again.',
          type: 'error',
          createdAt: null
        });
      }
    } catch (error) {
      console.error('Error uploading data to DB:', error);
      setUploadStatus({ message: 'An unexpected error occurred while saving data.', type: 'error', createdAt: null });
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <Box sx={{ p: 10 }}>
      <Typography variant="h4" gutterBottom>Upload Form </Typography>

      {/* Excel File Upload Section */}
      <Box sx={{ mb: 3 }}>
        <input
          type="file"
          accept=".xlsx, .xls, .csv"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          id="excel-file-upload-button"
          ref={excelFileInputRef}
        />
        <label htmlFor="excel-file-upload-button">
          <Button variant="contained" component="span" disabled={parsingLoading}>
            {parsingLoading ? <CircularProgress size={24} color="inherit" /> : 'Choose Excel/CSV File'}
          </Button>
        </label>
        {parsingLoading && <Typography variant="body2" sx={{ ml: 2, display: 'inline' }}>Parsing file...</Typography>}
        {fileError && <Alert severity="error" sx={{ mt: 2 }}>{fileError}</Alert>}
        {fileTypeMessage && <Alert severity="info" sx={{ mt: 2 }}>{fileTypeMessage}</Alert>}
      </Box>

      {/* Image Upload Section */}
      <Box sx={{ mb: 3 }}>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
          id="image-upload-button"
          ref={imageFileInputRef}
        />
        <label htmlFor="image-upload-button">
          <Button variant="contained" color="secondary" component="span" sx={{ mr: 2 }}>
            Upload Image (Optional)
          </Button>
        </label>
        {selectedImageFile && (
          <Typography variant="body2" sx={{ display: 'inline', fontStyle: 'italic' }}>
            {selectedImageFile.name} selected
          </Typography>
        )}
        {imageUploadError && <Alert severity="error" sx={{ mt: 2 }}>{imageUploadError}</Alert>}
      </Box>

      {/* Small Image Preview Area */}
      {imagePreviewUrl && (
        <Box sx={{ mb: 3, position: 'relative', width: 'fit-content', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Image Preview:</Typography>
          {/* Button/clickable image to open big preview */}
          <Button
            onClick={handleOpenBigImagePreview}
            sx={{ p: 0, minWidth: 0, display: 'block', '&:hover': { opacity: 0.8 } }}
          >
            <img src={imagePreviewUrl} alt="Selected Preview" style={{ maxWidth: '300px', maxHeight: '200px', display: 'block' }} />
          </Button>
          <IconButton
            onClick={handleClearImage}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              p: 0.5,
              color: 'error.main',
              backgroundColor: 'white',
              '&:hover': { backgroundColor: 'grey.100' },
            }}
            aria-label="clear image"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* --- NEW: Big Image Preview Dialog --- */}
      <Dialog
        open={openBigImagePreview}
        onClose={handleCloseBigImagePreview}
        maxWidth="md" // Adjust as needed
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={handleCloseBigImagePreview}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
              zIndex: 1,
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <img
            src={imagePreviewUrl}
            alt="Full size preview"
            style={{ width: '100%', height: 'auto', display: 'block' }} // Image fills dialog width
          />
        </DialogContent>
      </Dialog>

      {/* Save to Database button and status */}
      {(tableData.length > 0 || selectedImageFile) && !fileError && !parsingLoading && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUploadToDB}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save to Database'}
            </Button>
            {uploadStatus.message && (
              <Alert severity={uploadStatus.type} sx={{ flexGrow: 1 }}>
                {uploadStatus.message}
                {uploadStatus.createdAt && (
                  <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
                    Uploaded on: {new Date(uploadStatus.createdAt).toLocaleString()}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>

          {/* Table Data Display (Only if Excel/CSV data exists) */}
          {tableData.length > 0 && (
            <Box sx={{ overflowX: 'auto', border: '1px solid #ccc', mt: 2 }}>
              <table style={{ borderCollapse: 'collapse', minWidth: '100%', tableLayout: 'auto' }}>
                <tbody>
                  {tableData.map((rowObj, rowIndex) => (
                    <tr
                      key={`data-row-${rowIndex}`}
                      style={{ backgroundColor: 'transparent' }}
                    >
                      {rowObj.cells.map((cell, cellIndex) => {
                        if (cell === null) {
                          return null;
                        }
                        const hasFillColor = cell.fillColor !== null;

                        const cellStyle = {
                          border: '1px solid #ddd',
                          padding: '8px',
                          verticalAlign: 'top',
                          textAlign: 'left',
                          outline: 'none',
                          fontWeight: hasFillColor ? 'bold' : (cell.isCellBold ? 'bold' : 'normal'),
                          backgroundColor: hasFillColor ? '#ADD8E6' : (rowObj.isRowBold ? '#e0e0e0' : 'transparent'),
                          color: cell.fontColor || 'inherit',
                        };

                        return (
                          <td
                            key={`data-cell-${rowIndex}-${cellIndex}`}
                            rowSpan={cell.rowspan}
                            colSpan={cell.colspan}
                            style={cellStyle}
                            contentEditable="true"
                            onBlur={(e) => handleCellChange(e.target.innerText, rowIndex, cellIndex)}
                          >
                            {cell.value}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ExcelUploader;